import IconComponent from 'gcs-common/components/Icon/Icon';
import { ICON, ICON_COLOR } from 'gcs-common/constants/IconConstants';
import useMsTeamsClient from 'gcs-common/hooks/useMsTeamsClient';
import { useCallback, useEffect, useRef, useState } from 'react';
import edgeBellScreenshot from '../../../img/edge_permission_screenshot.png';
import browserScreenShot from '../../../img/permission_screenshot.png';
import styles from './styles.module.scss';

// Microsoft Edge enables "Quiet notification requests" by default: instead of a
// modal it shows a bell icon in the address bar and either leaves
// Notification.requestPermission() pending until the user acts on the bell or
// resolves it to 'default'. Either way no prompt is shown, so on Edge we surface
// the manual instructions (via a fallback timer for the pending case, and on any
// non-granted resolution) instead of leaving the banner unresponsive.
const isEdge = () => typeof navigator !== 'undefined' && /Edg\//.test(navigator.userAgent);
const QUIET_UI_FALLBACK_MS = 100;

const PushWarning = () => {
  const [showPermissionWarning, setShowPermissionWarning] = useState(false);
  const [showPermissionDeniedWarning, setShowPermissionDeniedWarning] = useState(false);
  const teamsClient = useMsTeamsClient();
  // TODO: Move into custom hook?
  useEffect(() => {
    if (!('Notification' in window)) {
      setShowPermissionWarning(false);
      return;
    }
    if (Notification.permission !== 'granted') {
      setShowPermissionWarning(true);
    }
  }, []);

  const quietUiTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(quietUiTimerRef.current), []);

  const handleAskForPushPermission = useCallback(() => {
    if (!('Notification' in window)) {
      return;
    }
    clearTimeout(quietUiTimerRef.current);
    if (isEdge()) {
      quietUiTimerRef.current = setTimeout(() => {
        setShowPermissionDeniedWarning(true);
      }, QUIET_UI_FALLBACK_MS);
    }
    (async () => {
      const permission = await Notification.requestPermission();
      clearTimeout(quietUiTimerRef.current);
      if (permission === 'granted') {
        setShowPermissionWarning(false);
        setShowPermissionDeniedWarning(false);
      } else if (permission === 'denied' || isEdge()) {
        // On Edge a non-granted resolution ('default') means the quiet UI
        // suppressed the prompt, so guide the user to the address-bar bell.
        setShowPermissionDeniedWarning(true);
      }
    })();
  }, []);

  const handleClosePushPermission = useCallback(() => {
    setShowPermissionWarning(false);
  }, []);

  const handleClosePushPermissionDenied = useCallback(() => {
    setShowPermissionDeniedWarning(false);
  }, []);

  if (showPermissionDeniedWarning) {
    return (
      <div className={styles.pushDeniedWarning}>
        <div className={styles.permissionDeniedHeader}>
          <IconComponent Icon={ICON.SETTINGS} alt="push-settings" />
          <div>Push-Benachrichtigungen blockiert</div>
          <button className={styles.closeIcon} type="button" onClick={handleClosePushPermissionDenied}>
            <IconComponent Icon={ICON.CLOSE} alt="close-push-settings" />
          </button>
        </div>
        <div className={styles.pushDeniedBody}>
          Push-Benachrichtigungen werden von deinem Browser blockiert.
          <br />
          Bitte aktiviere sie über deine Browsereinstellungen wie im Beispiel gezeigt. Das Menü kann sich je nach
          Browser unterscheiden.
          <div>
            <img className={styles.browserScreenShot} src={browserScreenShot} alt="browser-screenshot" />
          </div>
          {isEdge() && (
            <>
              <br />
              In Microsoft Edge findest du die Einstellung ggf. rechts in der Adressleiste – klicke dort auf das
              Glocken-Symbol, um Benachrichtigungen zu erlauben.
              <div>
                <img className={styles.browserScreenShot} src={edgeBellScreenshot} alt="edge-bell-screenshot" />
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (showPermissionWarning && !teamsClient.isInitialized) {
    return (
      <div className={styles.pushWarning}>
        <button className={styles.settingsIcon} type="button" onClick={handleAskForPushPermission}>
          <IconComponent Icon={ICON.SETTINGS} color={ICON_COLOR.WHITE} alt="push-settings" />
        </button>
        <button className={styles.warningText} type="button" onClick={handleAskForPushPermission}>
          In deinem Browser sind Push-Benachrichtigungen nicht aktiviert.
          <br />
          Klicke hier, um Push-Benachrichtigungen zu erlauben.
        </button>
        <button className={styles.closeIcon} type="button" onClick={handleClosePushPermission}>
          <IconComponent Icon={ICON.CLOSE} color={ICON_COLOR.WHITE} alt="close-push-settings" />
        </button>
      </div>
    );
  }

  return null;
};

export default PushWarning;
